import { Component, NgZone, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { ToastrService } from 'ngx-toastr';
import { ConsoleLogger } from '../../core/utilities/console-logger';
import { ConversionService } from './conversion.service';
import { ConversionClient } from './models/ConversionClient';
import { ConversionDetailModalComponent } from './conversion-detail-modal/conversion-detail-modal.component';
import { MigrationProgressModalComponent } from './migration-progress-modal/migration-progress-modal.component';
import {
	ConversionCodeConflict,
	ConversionCodeOwner,
	ConversionException,
	ConversionMigrationResult,
	ConversionMigrationStep,
	ConversionRecodeCompanyResult,
	ConversionSkippedInvoice,
} from './models/ConversionMigrationResult';
import {
	conversionExceptions,
	downloadConversionListCsv,
	downloadExceptionsCsv,
	downloadSkippedInvoicesCsv,
	downloadInvoiceExportSummaryCsv,
	invoiceExportSummaryRows,
	readDownloadError,
	saveDownloadedFile,
	skippedNegativeInvoices,
} from './skipped-invoices-csv';
import { ConversionBulkStepProgress, ConversionMigrationProgress } from './models/ConversionMigrationProgress';
import { ConfirmDialogService } from '../../core/components/confirm-dialog/confirm-dialog.service';

@Component({
	selector: 'app-conversion',
	templateUrl: './conversion.component.html',
	styleUrls: ['./conversion.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ConversionComponent implements OnInit {
	@ViewChild(ConversionDetailModalComponent)
	conversionDetailModal: ConversionDetailModalComponent;

	@ViewChild(MigrationProgressModalComponent)
	migrationProgressModal: MigrationProgressModalComponent;

	@ViewChild('CodeConflictModalTemplate')
	codeConflictModalTemplate: TemplateRef<any>;

	/** "Company code already registered" popup: the carrier, the code and who has it. */
	public codeConflict: ConversionCodeConflict;
	public codeConflictClientName = '';
	public recodeRunning = false;
	public recodeError = '';
	private codeConflictModalRef: NgbModalRef;

	public columnMode = ColumnMode;
	public loadingIndicator = false;
	public clientNameFilter = '';
	public showRemoved = false;

	/**
	 * 1 = Part 1 view: carriers that still need Part 1.
	 * 2 = Part 2 view: carriers through Part 1, waiting on the manual invoice import and Part 2.
	 * 3 = Converted view: carriers that finished, which can be reopened to run again.
	 * 4 = Full Conversion view: every carrier not yet converted, run start to finish in one go
	 *     (Part 1, the invoices posted through the API, then Part 2).
	 * 5 = All Clients view: every WinFactor carrier on the list, converted or not, so any of them
	 *     can be run through the full conversion again (converted ones are reopened first).
	 * Archived carriers are shown together, whatever their part.
	 */
	public view = 4;

	/**
	 * The Part 1 / Part 2 tabs are hidden for now (not removed): carriers are converted from the
	 * Ready for Conversion tab in one run. Set to true to show them again.
	 */
	public showPartTabs = false;

	/** Carriers that finished converting, for the Converted tab. Loaded alongside the queue. */
	public convertedRows: ConversionClient[] = [];
	public reopenRunning = false;

	public rows: ConversionClient[] = [];
	public filteredRows: ConversionClient[] = [];
	public selectedRow: ConversionClient;

	public migrationRunning = false;
	public migrationCompleted = 0;
	public migrationTotal = 0;
	public migrationResults: ConversionMigrationResult[] = [];
	/** Part and carrier currently running, for the on-page progress panel. */
	public migrationPart = 1;
	public migrationCurrentName = '';
	public showMigrationResults = false;

	/**
	 * The conversion steps, in order, for the bulk buttons: each runs that one step for every carrier on the conversion
	 * list. Mirrors ConversionMigrationStepNames on the server.
	 */
	public readonly bulkSteps: { step: number; label: string; hint: string }[] = [
		{ step: 1, label: 'Import Clients', hint: 'Creates (or finds) each carrier in FactorCloud on its transfer term, with its account executive and funding instructions, and maps it in Aladdin. Every run fully updates the client from WinFactor and turns generate invoice off until step 23.' },
		{ step: 2, label: 'Set Client doNotBill', hint: 'Sets doNotBill to true on each carrier\'s FactorCloud client (only doNotBill is sent), in every environment, until step 23 sets it back to false. Run it right after Import Clients.' },
		{ step: 3, label: 'Set Client ACH & Wire Fees', hint: 'Puts the ACH Fee and Wire Fee from the transfer terms file (Data Upload) on each carrier\'s FactorCloud client (only achFee / wireFee are sent; a blank fee is left as it is). A carrier with no fees in the file is skipped. Run it right after Set Client doNotBill.' },
		{ step: 4, label: 'Import Debtors', hint: 'Pushes the debtors of the carriers that have a FactorCloud client and are not in FactorCloud yet: no FactorCloudID on dbo.Debtors, or one FactorCloud does not have. A debtor found in FactorCloud (by id, name or code) is never inserted or updated. Turns doNotBill on for the debtors it creates (step 23 leaves it on).' },
		{ step: 5, label: 'Import Client Contacts', hint: 'Sends each carrier\'s WinFactor client contacts to its FactorCloud client: adds the ones FactorCloud does not have and, run again, updates the ones that changed (matched by name). In Production existing contacts are never updated.' },
		{ step: 6, label: 'Import Debtor Contacts', hint: 'Sends the WinFactor customer contacts to each carrier\'s FactorCloud debtors (each debtor once): adds the missing ones and, run again, updates the ones that changed (never in Production). Only debtors the conversion inserted get contacts; a debtor that already existed is never touched.' },
		{ step: 7, label: 'Map Debtor/Client Relationships', hint: 'Links every debtor to its carrier in FactorCloud, the default debtor (Aladdin Financial Inc) included, with the WinFactor credit limit and No Buy. Only missing links are added; an existing link is never overwritten.' },
		{ step: 8, label: 'Import Client Notes', hint: 'Sends the WinFactor client notes to each FactorCloud client\'s General Notes.' },
		{ step: 9, label: 'Import Customer Notes', hint: 'Sends the WinFactor customer notes to each carrier\'s FactorCloud debtors as General Notes (each debtor once). Only debtors the conversion inserted get notes; a debtor that already existed is never touched.' },
		{ step: 10, label: 'Import Client Documents', hint: 'Uploads each carrier\'s documents from blob storage to FactorCloud (only the ones not sent yet).' },
		{ step: 11, label: 'Import Customer Documents', hint: 'Uploads each carrier\'s debtors\' WinFactor customer documents from blob storage to their FactorCloud debtors (only the ones not sent yet; each debtor once), existing debtors included. Marks Part 1 complete.' },
		{ step: 12, label: 'Create Invoice Export Files', hint: 'Writes every open WinFactor invoice not yet in FactorCloud, at its current balance (preferred payment method CHECK), to the CLIENT_INVOICES export files in the invoices folder. Invoices that cannot go in the CSV are listed in a "not sent" error file to download.' },
		{ step: 13, label: 'Create $0 First Invoice', hint: 'Creates the $0 invoice dated on the first fund date, against the default debtor, and approves it for funding.' },
		{ step: 14, label: 'Import Invoices', hint: 'Imports every export file waiting in the invoices folder into FactorCloud and moves it to processed-invoices (failed rows to an error file to download). Fails for a carrier whose invoices FactorCloud did not import. Step 15 puts them on the transfer term and step 16 syncs them back.' },
		{ step: 15, label: 'Put Invoices on Transfer Term', hint: 'Reads every FactorCloud invoice in Pending status, whatever its date, once, then puts each carrier\'s own client\'s on its transfer term (term only: their notes are added in step 18). Invoices already on the term are left as they are.' },
		{ step: 16, label: 'Sync Invoices Back', hint: 'Reads each carrier\'s invoices back from FactorCloud into Aladdin and maps every WinFactor invoice to its FactorCloud row. Step 17 approves them for funding.' },
		{ step: 17, label: 'Approve Imported Invoices', hint: 'Asks how many hours back to look (3 by default), then approves for funding every FactorCloud invoice in Pending status created in that time, for every client (not only the carriers on the list), against each client\'s CHECK funding instruction. Run it after Import Invoices.' },
		{ step: 18, label: 'Import Invoice Notes', hint: 'Sends the WinFactor invoice notes to the invoices already in FactorCloud, followed by NEW SYSTEM TRANSFER (NEW SYSTEM TRANSFER alone for an invoice with no notes).' },
		{ step: 19, label: 'Reset Default Funding Instruction', hint: 'Makes each client\'s AtoB instruction the default, else its first ACH, else its first WIRE (otherwise it stays CHECK).' },
		{ step: 20, label: 'Update Term to Live Term', hint: 'Moves each carrier\'s FactorCloud client from its transfer term to its live term: the Term Name of the transfer terms file (Data Upload), found by name in FactorCloud (only termId is sent). A carrier with no live term in the file is skipped; one whose live term FactorCloud does not have fails.' },
		{ step: 21, label: 'Update Client Dates & Balance', hint: 'Copies the WinFactor carrier\'s FirstInvoiceDate and ContractDate onto its FactorCloud carrier row in Aladdin, and moves its CurrentBalanceAmount there (the WinFactor row is set to 0).' },
		{ step: 22, label: 'Deactivate Clients', hint: 'Deactivates the WinFactor carrier in Aladdin.' },
		{ step: 23, label: 'Set Client Emails & Turn Billing On', hint: 'Client emails and client contact emails are loaded as it@aladdincap.com: this puts the real WinFactor emails on the FactorCloud client and its contacts (every environment), turns generate invoice on and doNotBill off for the client (its debtors\' doNotBill is left as it is), and marks the carrier converted, so it leaves the conversion list. Run it again after rerunning steps 1 or 5.' },
		{ step: 24, label: 'Add Funding Options', hint: 'Adds every active WinFactor funding option (Funding Options file, secure.WFBD) of each carrier to its FactorCloud client, whatever its funding type, and makes the one with the lowest option number the default. An option already in FactorCloud is left as it is; if the lowest one cannot be made the default, a new one is created with its values under the next free option number and made the default. Nothing else is changed.' },
	];

	/** The bulk step running (or last run), shown in its own panel. */
	public bulkProgress: ConversionBulkStepProgress = null;
	public bulkRunning = false;
	public showBulkProgress = false;
	/** Step number of the bulk step running (or last run). */
	public bulkStep = 0;
	/** Run id of the bulk step running now, for its Stop button. */
	public bulkRunId: string = null;
	public stoppingBulk = false;

	/** Bulk steps stop after the first carrier that fails. */
	public stopImportOnFailure = false;

	/**
	 * Runs again what is already done: bulk steps also run the carriers an earlier run of the step succeeded for, and a
	 * carrier's own run (or batch) also runs the steps it already succeeded at. Off, both leave those out.
	 */
	public includeCompletedCarriers = false;

	/** Step 17 (Approve Imported Invoices): how many hours back it looks, as entered when the run started. */
	public approveLookbackHours: number = null;
	private readonly approveInvoicesStep = 17;

	private static readonly bulkStepsRunKey = 'conversion.bulkStepsRun';

	/**
	 * The bulk steps run in this browser session (by label, so a renumbered step is not mixed up), kept in sessionStorage
	 * so they survive a refresh or leaving the page; their buttons are shown filled in.
	 */
	private bulkStepsRun: string[] = ConversionComponent.readBulkStepsRun();

	constructor(
		private conversionService: ConversionService,
		private toastrService: ToastrService,
		private confirmDialogService: ConfirmDialogService,
		private ngZone: NgZone,
		private modalService: NgbModal
	) {}

	ngOnInit() {
		this.loadConversionClients();
	}

	/** True when the bulk step has been run in this browser session: its button is then blue with white text. */
	isBulkStepRun(step: number): boolean {
		const found = this.bulkSteps.filter((x) => x.step === step)[0];
		return !!found && this.bulkStepsRun.indexOf(found.label) >= 0;
	}

	private markBulkStepRun(step: number) {
		const found = this.bulkSteps.filter((x) => x.step === step)[0];
		if (!found || this.bulkStepsRun.indexOf(found.label) >= 0) {
			return;
		}
		this.bulkStepsRun = this.bulkStepsRun.concat([found.label]);
		try {
			sessionStorage.setItem(ConversionComponent.bulkStepsRunKey, JSON.stringify(this.bulkStepsRun));
		} catch (e) {
			// Storage unavailable: the buttons still show it until the page is left.
		}
	}

	private static readBulkStepsRun(): string[] {
		try {
			const saved = JSON.parse(sessionStorage.getItem(ConversionComponent.bulkStepsRunKey) || '[]');
			return Array.isArray(saved) ? saved : [];
		} catch (e) {
			return [];
		}
	}

	/** "Step 4 - Map Debtor/Client Relationships", for the panel and the toasts. */
	bulkStepLabel(step: number): string {
		const found = this.bulkSteps.filter((x) => x.step === step)[0];
		return 'Step ' + step + (found ? ' - ' + found.label : '');
	}

	/**
	 * Bulk step: runs one conversion step for every carrier on the conversion list. The server runs the carriers one at a
	 * time in the background; the panel follows its progress. Every step reuses what FactorCloud already has, so it is
	 * safe to run again.
	 */
	startBulkStep(step: number) {
		if (this.bulkRunning || this.migrationRunning) {
			return;
		}

		// Step 17 asks how many hours back to look for the Pending invoices to approve.
		let hours: number = null;
		if (step === this.approveInvoicesStep) {
			hours = this.askApproveLookbackHours();
			if (hours === null) {
				return;
			}
		}

		const found = this.bulkSteps.filter((x) => x.step === step)[0];
		const extra =
			step === 11
				? ' Carriers it succeeds for are marked Part 1 complete.'
				: step === 23
				? ' Carriers it succeeds for are marked converted and leave the conversion list.'
				: step === 18 || step === 21 || step === 22 || step === 24
				? ' It runs on every company in FactorCloud.TermsLookup, converted or archived included, every time. Carriers without a FactorCloud client yet are skipped.'
				: step !== 1 && step !== 4
				? ' Carriers without a FactorCloud client yet are skipped.'
				: '';
		this.confirmDialogService.showConfirm(
			(hours !== null
				? `Run ${this.bulkStepLabel(step)}? Every FactorCloud invoice in Pending status created in the last ${hours} hour(s) is approved for funding, for every client, against each client's CHECK funding instruction.`
				: `Run ${this.bulkStepLabel(step)} for every carrier on the conversion list (${this.rows.length})? ${found ? found.hint : ''}${extra}`) +
				(step === 18 || step === 21 || step === 22 || step === 24 || this.includeCompletedCarriers
					? ' Carriers an earlier run of this step already did are run again.'
					: ' Carriers an earlier run of this step already did are left out.') +
				' It runs in the background, several carriers at a time.',
			() => {
				this.bulkRunning = true;
				this.showBulkProgress = true;
				this.bulkProgress = null;
				this.bulkStep = step;
				this.stoppingBulk = false;
				this.conversionService
					.startBulkStep(step, this.stopImportOnFailure, this.includeCompletedCarriers, hours)
					.toPromise()
					.then(
						(started) => {
							this.bulkRunId = started.runId;
							this.pollBulkStep(started.runId, 0);
						},
						(error) => {
							this.bulkRunning = false;
							this.toastrService.error(
								error && typeof error.error === 'string' ? error.error : 'The bulk step could not be started.',
								this.bulkStepLabel(step)
							);
						}
					);
			},
			() => {
				return;
			}
		);
	}

	private pollBulkStep(runId: string, errors: number) {
		this.conversionService
			.getBulkStepProgress(runId)
			.toPromise()
			.then(
				(progress) => {
					if (progress) {
						this.bulkProgress = progress;
						// Another bulk step was already running: the server handed back that one, so follow it.
						this.bulkStep = progress.step;
					}
					if (progress && progress.isComplete) {
						const title = this.bulkStepLabel(progress.step);
						// It ran (whatever the outcome), unless it could not start at all.
						if (!progress.errorMessage) {
							this.markBulkStepRun(progress.step);
						}
						this.bulkRunning = false;
						this.stoppingBulk = false;
						this.bulkRunId = null;
						if (progress.errorMessage) {
							this.toastrService.error(progress.errorMessage, title);
						} else if (progress.stopped || progress.failures.length > 0) {
							this.toastrService.warning(progress.summary, title);
						} else {
							this.toastrService.success(progress.summary, title);
						}
						this.loadConversionClients();
						return;
					}
					setTimeout(() => this.pollBulkStep(runId, 0), 2000);
				},
				(error) => {
					// The step keeps running on the server; only give up after about five minutes of failed polls.
					if (errors + 1 >= 150) {
						this.bulkRunning = false;
						this.toastrService.error('Lost track of the bulk step. It may still be running on the server.', this.bulkStepLabel(this.bulkStep));
						return;
					}
					setTimeout(() => this.pollBulkStep(runId, errors + 1), 2000);
				}
			);
	}

	/** Stop button: the bulk step stops after the carrier it is on, so nothing is left half done. */
	stopBulkStep() {
		if (!this.bulkRunId || this.stoppingBulk) {
			return;
		}
		this.stoppingBulk = true;
		this.conversionService
			.stopBulkStep(this.bulkRunId)
			.toPromise()
			.then(
				() => this.toastrService.info('Stopping after the carrier it is on.', this.bulkStepLabel(this.bulkStep)),
				(error) => {
					this.stoppingBulk = false;
					this.toastrService.error(
						error && typeof error.error === 'string' ? error.error : 'Could not stop the bulk step.',
						this.bulkStepLabel(this.bulkStep)
					);
				}
			);
	}

	get bulkPercent(): number {
		if (this.bulkShowsPrep) {
			return Math.round((Math.min(this.bulkProgress.prepCompleted, this.bulkProgress.prepTotal) / this.bulkProgress.prepTotal) * 100);
		}

		return this.bulkProgress && this.bulkProgress.total > 0
			? Math.round((this.bulkProgress.completed / this.bulkProgress.total) * 100)
			: 0;
	}

	/** True while the bulk step does its work before the carriers run (step 4: the distinct debtors): the bar counts that. */
	get bulkShowsPrep(): boolean {
		return !!this.bulkProgress && !this.bulkProgress.isComplete && this.bulkProgress.prepTotal > 0;
	}

	downloadBulkExceptions() {
		if (this.bulkProgress && this.bulkProgress.exceptions.length > 0) {
			downloadExceptionsCsv(this.bulkProgress.exceptions, 'bulk_step_' + this.bulkProgress.step);
		}
	}

	downloadBulkSkippedInvoices() {
		if (this.bulkProgress && this.bulkProgress.skippedNegativeInvoices.length > 0) {
			downloadSkippedInvoicesCsv(this.bulkProgress.skippedNegativeInvoices, 'bulk_step_' + this.bulkProgress.step);
		}
	}

	/** Bulk step 10: downloads one invoice import error file (FactorCloud's failed rows, saved in processed-invoices). */
	/** The invoice summary report lines of the conversion results (one carrier's, or every carrier's). */
	invoiceSummaryRows(result?: ConversionMigrationResult) {
		return invoiceExportSummaryRows(result ? [result] : this.migrationResults);
	}

	/** Downloads the invoice summary report for the conversion results, the same report Create Invoice Export Files saves. */
	downloadInvoiceSummary(result?: ConversionMigrationResult) {
		downloadInvoiceExportSummaryCsv(this.invoiceSummaryRows(result), result ? result.clientName : 'all_carriers');
	}

	/** How long the conversion results took: from the first carrier's start to the last one's finish. */
	get migrationRanFor(): string {
		const started = this.migrationResults.map((x) => x.startedOn).filter((x) => !!x).sort();
		const finished = this.migrationResults.map((x) => x.finishedOn).filter((x) => !!x).sort();
		if (started.length === 0 || finished.length === 0) {
			return '';
		}
		return this.formatRanFor(new Date(this.utc(finished[finished.length - 1])).getTime() - new Date(this.utc(started[0])).getTime());
	}

	/** How long the bulk step has run (so far, or in all). */
	get bulkRanFor(): string {
		if (!this.bulkProgress || !this.bulkProgress.startedOn) {
			return '';
		}
		const started = new Date(this.utc(this.bulkProgress.startedOn)).getTime();
		const finished = this.bulkProgress.finishedOn ? new Date(this.utc(this.bulkProgress.finishedOn)).getTime() : Date.now();
		return this.formatRanFor(finished - started);
	}

	/** A run time as "1h 02m 03s", "2m 05s" or "12s". */
	formatRanFor(ms: number): string {
		const total = Math.max(0, Math.round((ms || 0) / 1000));
		const hours = Math.floor(total / 3600);
		const minutes = Math.floor((total % 3600) / 60);
		const seconds = total % 60;
		const pad = (value: number) => (value < 10 ? '0' : '') + value;
		return hours > 0 ? hours + 'h ' + pad(minutes) + 'm ' + pad(seconds) + 's' : minutes > 0 ? minutes + 'm ' + pad(seconds) + 's' : seconds + 's';
	}

	/** A server UTC time, read as UTC even when it comes without a zone. */
	utc(value: string): string {
		return /Z$|[+-]\d\d:\d\d$/.test(value) ? value : value + 'Z';
	}

	/** True for the export summary report (step 12) among the invoice files offered for download. */
	isSummaryFile(fileName: string): boolean {
		return !!fileName && /_summary\.csv$/i.test(fileName);
	}

	/** What an invoice file offered for download is: the export summary, the invoices not sent, or the import errors. */
	invoiceFileLabel(fileName: string): string {
		if (this.isSummaryFile(fileName)) {
			return 'export summary';
		}
		return fileName && fileName.indexOf('_not_sent_') >= 0 ? 'invoices not sent' : 'invoice import errors';
	}

	/** Error file being downloaded from the bulk panel, so its link shows a spinner. */
	public downloadingBulkErrorFile: string = null;

	downloadBulkInvoiceImportErrors(fileName: string) {
		if (this.downloadingBulkErrorFile) {
			return;
		}
		this.downloadingBulkErrorFile = fileName;
		this.conversionService
			.downloadInvoiceImportErrorFile(fileName)
			.toPromise()
			.then(
				(blob) => {
					this.downloadingBulkErrorFile = null;
					saveDownloadedFile(blob, fileName);
				},
				(error) => {
					this.downloadingBulkErrorFile = null;
					ConsoleLogger.logInfo('conversionService.downloadInvoiceImportErrorFile failed', error);
					readDownloadError(error, 'The invoice import error file could not be downloaded.').then((message) =>
						this.toastrService.error(message, 'Download failed')
					);
				}
			);
	}

	loadConversionClients() {
		this.loadConvertedClients();
		this.loadingIndicator = true;
		this.conversionService
			.getConversionClientList(this.showRemoved)
			.toPromise()
			.then(
				(results) => {
					ConsoleLogger.logInfo(
						'conversionService.getConversionClientList',
						results
					);
					// A carrier whose run failed keeps its Failed status (and the reason) through the
					// reload, rather than dropping back to Not Started.
					const failedByKeys: { [keys: string]: ConversionClient } = {};
					this.rows
						.filter((old) => old.migrationStatus === 'Failed')
						.forEach((old) => (failedByKeys[this.rowKey(old)] = old));

					this.rows = (results || []).map((result) => {
						const row = new ConversionClient();
						Object.assign(row, result);
						const failed = failedByKeys[this.rowKey(row)];
						if (failed) {
							row.migrationStatus = failed.migrationStatus;
							row.migrationSummary = failed.migrationSummary;
						}
						row.selected = false;
						return row;
					});
					this.updateFilter();
					this.loadingIndicator = false;
				},
				(error) => {
					console.error('Error loading the conversion list:', error);
					this.toastrService.error(
						'Could not refresh the conversion list. Use the refresh button to try again.',
						'Load Failed'
					);
					this.updateFilter();
					this.loadingIndicator = false;
				}
			);
	}

	/** Converted carriers for the Converted tab. Not loaded while archived carriers are shown. */
	private loadConvertedClients() {
		if (this.showRemoved) {
			this.convertedRows = [];
			return;
		}

		this.conversionService
			.getConversionClientList(false, true)
			.toPromise()
			.then(
				(results) => {
					this.convertedRows = (results || []).map((result) => {
						const row = new ConversionClient();
						Object.assign(row, result);
						row.selected = false;
						return row;
					});
					this.updateFilter();
				},
				(error) => {
					console.error('Error loading the converted carriers:', error);
					this.convertedRows = [];
					this.updateFilter();
				}
			);
	}

	/** The rows behind the current tab: the Converted tab has its own list. */
	get activeRows(): ConversionClient[] {
		if (this.showRemoved) {
			return this.rows;
		}
		if (this.view === 5) {
			return this.allRows;
		}
		return this.view === 3 ? this.convertedRows : this.rows;
	}

	/** All Clients tab: the carriers still on the list plus the converted ones. */
	get allRows(): ConversionClient[] {
		return this.rows.concat(this.convertedRows);
	}

	get allCount(): number {
		return this.rows.length + this.convertedRows.length;
	}

	get convertedCount(): number {
		return this.convertedRows.length;
	}

	updateFilter() {
		// Selected carriers sit at the top; each group is alphabetical by carrier name.
		this.filteredRows = this.activeRows
			.filter((row) => this.inView(row))
			.filter((row) => this.matchesText(row.clientName, this.clientNameFilter))
			.sort((a, b) => {
				if (a.selected !== b.selected) {
					return a.selected ? -1 : 1;
				}
				return (a.clientName || '').localeCompare(b.clientName || '', 'en', {
					sensitivity: 'base',
				});
			});
	}

	/** Archived carriers ignore the Part 1 / Part 2 tabs. */
	private inView(row: ConversionClient) {
		if (this.showRemoved || this.view === 3 || this.view === 4 || this.view === 5) {
			return true;
		}
		return this.view === 2
			? !!row.conversionPart1CompletedDate
			: !row.conversionPart1CompletedDate;
	}

	get part1Count(): number {
		return this.rows.filter((row) => !row.conversionPart1CompletedDate).length;
	}

	get part2Count(): number {
		return this.rows.filter((row) => !!row.conversionPart1CompletedDate).length;
	}

	/** Every carrier not yet converted can run the full conversion. */
	get fullCount(): number {
		return this.rows.length;
	}

	/** "Part 1", "Part 2" or "Full Conversion" (part 3). */
	partLabel(part: number): string {
		return part === 3 ? 'Full Conversion' : 'Part ' + part;
	}

	setView(view: number) {
		if (this.view === view || this.migrationRunning) {
			return;
		}
		this.view = view;
		// A selection never carries across tabs, so each Run button only acts on what is on screen.
		this.rows.forEach((row) => (row.selected = false));
		this.convertedRows.forEach((row) => (row.selected = false));
		this.showMigrationResults = false;
		this.updateFilter();
	}

	private matchesText(value: string, filter: string) {
		const needle = (filter || '').trim().toLowerCase();
		return (
			needle.length === 0 || (value || '').toLowerCase().indexOf(needle) >= 0
		);
	}

	toggleShowRemoved() {
		this.showRemoved = !this.showRemoved;
		this.showMigrationResults = false;
		this.loadConversionClients();
	}

	archiveRow(row: ConversionClient) {
		if (!row || this.migrationRunning) {
			return;
		}

		this.confirmDialogService.showConfirm(
			`Archive ${row.clientName}? It comes off the conversion list and will not be converted to FactorCloud. You can restore it later from "Show archived carriers".`,
			() => {
				this.setRowRemoved(row, true);
			},
			() => {
				return;
			}
		);
	}

	restoreRow(row: ConversionClient) {
		if (!row) {
			return;
		}
		this.setRowRemoved(row, false);
	}

	private setRowRemoved(row: ConversionClient, removed: boolean) {
		const request = removed
			? this.conversionService.excludeConversionClient(row.clientKeys)
			: this.conversionService.restoreConversionClient(row.clientKeys);

		request.toPromise().then(
			() => {
				this.toastrService.success(
					removed
						? `${row.clientName} was archived and taken off the conversion list.`
						: `${row.clientName} is back on the conversion list.`,
					removed ? 'Carrier Archived' : 'Carrier Restored'
				);
				this.rows = this.rows.filter((x) => x !== row);
				this.updateFilter();
			},
			(error) => {
				console.error('Error updating the conversion list:', error);
				this.toastrService.error(
					error && typeof error.error === 'string'
						? error.error
						: removed
							? 'Could not archive the carrier.'
							: 'Could not restore the carrier to the conversion list.',
					removed ? 'Archive Failed' : 'Restore Failed'
				);
			}
		);
	}

	toggleRow(row: ConversionClient) {
		row.selected = !row.selected;
		this.reorderAfterSelection();
	}

	toggleSelectAll() {
		const selectAll = !this.allSelected();
		this.filteredRows.forEach((row) => (row.selected = selectAll));
		this.reorderAfterSelection();
	}

	// ngx-datatable reuses its row elements by position (its trackByProp is broken in
	// 11.x), so re-sorting inside the click handler left the clicked checkbox ticked on
	// whichever carrier moved into that slot. Letting the click finish and its binding
	// settle first means every checkbox is back in step with its row before they move.
	private reorderAfterSelection() {
		setTimeout(() => this.updateFilter());
	}

	allSelected() {
		return (
			this.filteredRows.length > 0 &&
			this.filteredRows.every((row) => row.selected === true)
		);
	}

	someSelected() {
		return (
			this.filteredRows.some((row) => row.selected === true) &&
			!this.allSelected()
		);
	}

	get selectedRows(): ConversionClient[] {
		return this.activeRows.filter((row) => row.selected === true);
	}

	/**
	 * Converted tab: puts the selected carriers back on the list at Part 1 or Part 2. Only
	 * Aladdin's markers change. The carrier is then run with the normal Run button; every step
	 * reuses what FactorCloud already has, and notes are only added where FactorCloud has none.
	 */
	reopenSelected(part: number) {
		const selected = this.view === 3 ? this.selectedRows : [];
		if (selected.length === 0 || this.reopenRunning || this.migrationRunning) {
			return;
		}

		const which = selected.length === 1 ? selected[0].clientName : selected.length + ' carriers';
		const message =
			part === 2
				? `Reopen ${which} to Part 2? It moves to the Part 2 tab so Part 2 can be run again (matching invoices, deactivating the WinFactor carrier, client dates and balance). Invoice notes are only added where FactorCloud has none.`
				: `Reopen ${which} to Part 1? It moves to the Part 1 tab so both parts can be run again. The existing FactorCloud client, brokers and $0 invoice are reused; notes are only added where FactorCloud has none.`;

		this.confirmDialogService.showConfirm(
			message,
			() => {
				this.reopenRunning = true;
				Promise.all(
					selected.map((row) =>
						this.conversionService.reopenConversionClient(row.clientKeys, part).toPromise()
					)
				).then(
					() => {
						this.reopenRunning = false;
						this.toastrService.success(
							this.showPartTabs
								? `${which} reopened. Run it from the Part ${part} tab.`
								: `${which} reopened. Run it from the Ready for Conversion tab.`,
							'Carrier Reopened'
						);
						this.setView(this.showPartTabs ? part : 4);
						this.loadConversionClients();
					},
					(error) => {
						this.reopenRunning = false;
						console.error('Error reopening the carrier:', error);
						this.toastrService.error(
							error && typeof error.error === 'string' ? error.error : 'Could not reopen the carrier.',
							'Reopen Failed'
						);
						this.loadConversionClients();
					}
				);
			},
			() => {
				return;
			}
		);
	}

	get selectedCount(): number {
		return this.selectedRows.length;
	}

	/** Selected carriers that still need Part 1. */
	get part1SelectedRows(): ConversionClient[] {
		return this.selectedRows.filter((row) => !row.conversionPart1CompletedDate);
	}

	/** Selected carriers whose Part 1 is done, so their invoices can have been imported by hand. */
	get part2SelectedRows(): ConversionClient[] {
		return this.selectedRows.filter((row) => !!row.conversionPart1CompletedDate);
	}

	/**
	 * All Clients tab: runs the full conversion again for the selected carriers, converted or not.
	 * Converted carriers are reopened first (the run only picks up carriers not marked converted);
	 * every step reuses what FactorCloud already has, so this mainly adds what was missed, such as
	 * the AtoB funding instruction, and marks the carrier converted again at the end.
	 */
	rerunSelected() {
		const selected = this.view === 5 ? this.selectedRows : [];
		if (selected.length === 0) {
			this.toastrService.warning('Select at least one carrier.', 'Nothing Selected');
			return;
		}
		if (this.migrationRunning || this.reopenRunning) {
			return;
		}

		const converted = selected.filter((row) => !!row.conversionCompletedDate);
		const which = selected.length === 1 ? selected[0].clientName : selected.length + ' carriers';
		const message =
			`Rerun the full conversion for ${which}?` +
			(converted.length > 0 ? ` ${converted.length} converted carrier(s) are reopened first.` : '') +
			' The existing FactorCloud client, brokers, invoices, notes and funding instructions are reused, not added again; anything missing (such as the AtoB funding instruction) is added, and each carrier is marked converted again at the end. Continue?';

		this.confirmDialogService.showConfirm(
			message,
			() => {
				this.reopenRunning = true;
				Promise.all(
					converted.map((row) =>
						this.conversionService.reopenConversionClient(row.clientKeys, 1).toPromise()
					)
				).then(
					() => {
						this.reopenRunning = false;
						converted.forEach((row) => {
							row.conversionCompletedDate = null;
						});
						this.startMigration(selected, 3);
					},
					(error) => {
						this.reopenRunning = false;
						console.error('Error reopening the carriers for the rerun:', error);
						this.toastrService.error(
							error && typeof error.error === 'string' ? error.error : 'Could not reopen the converted carriers, so nothing was run.',
							'Rerun Failed'
						);
						this.loadConversionClients();
					}
				);
			},
			() => {
				return;
			}
		);
	}

	openDetailModal(row: ConversionClient) {
		this.ngZone.run(() => {
			this.selectedRow = row;
			if (this.selectedRow) {
				this.conversionDetailModal.openConversionDetailModal(this.selectedRow);
			}
		});
	}

	migrateSelected(part: number) {
		const selected =
			part === 3 ? this.selectedRows : part === 2 ? this.part2SelectedRows : this.part1SelectedRows;

		if (selected.length === 0) {
			this.toastrService.warning(
				part === 3
					? 'Select at least one carrier.'
					: part === 2
						? 'Select at least one carrier whose Part 1 is done.'
						: 'Select at least one carrier that has not run Part 1 yet.',
				'Nothing Selected'
			);
			return;
		}

		if (this.migrationRunning) {
			return;
		}

		const which = selected.length === 1 ? 'this carrier' : 'these ' + selected.length + ' carriers';
		const message =
			part === 3
				? `Run the full conversion for ${which}? In order: 1 the client, 2 sets the client's doNotBill to true, 3 sets the client's ACH and wire fees from the transfer terms file, 4 its missing debtors, 5 the client contacts, 6 the debtor contacts, 7 the client/debtor relationships, 8 the client notes, 9 the customer notes, 10-11 the client and customer documents, 12 WRITES EVERY OPEN INVOICE to the invoice export files at its current balance, 13 the $0 first-fund-date invoice, 14 IMPORTS THEM INTO FACTORCLOUD, 15 puts every Pending invoice of the client (any date) on the transfer term, 16 syncs them back to Aladdin, 17 approves every Pending invoice created in the hours you enter next (every client) for funding, 18 the invoice notes (with NEW SYSTEM TRANSFER), 19 resets the default funding instruction, 20 updates the client dates and balance, 21 deactivates the WinFactor carrier, 22 sets the client's real emails (they load as it@aladdincap.com), turns generate invoice on and the client's and debtors' doNotBill off, and marks it converted. Do not use this for carriers whose invoices are being imported by hand. That cannot be undone from here. Continue?`
				: part === 2
				? `Run Part 2 (steps 12-23) for ${which}? Only do this once their invoices have been imported into FactorCloud. It matches the imported invoices to the WinFactor invoices, creates the $0 first-fund-date invoice, adds the invoice notes, resets the default funding instruction, puts the client on its live term, updates the client dates and balance, deactivates the WinFactor carrier, sets the client's real emails, turns billing back on and marks it converted. Continue?`
				: `Run Part 1 (steps 1-11) for ${which}? It creates the carrier (with doNotBill set to true, then its ACH and wire fees) and its missing debtors in FactorCloud, adds their contacts, links them, adds the client and customer notes and uploads the client and customer documents. Invoices are NOT imported - import them into FactorCloud by hand, then run Part 2. That cannot be undone from here. Continue?`;

		this.confirmDialogService.showConfirm(
			message,
			() => {
				this.startMigration(selected, part);
			},
			() => {
				return;
			}
		);
	}

	/**
	 * Asks how many hours back step 17 looks for Pending invoices created (imported) in FactorCloud. Null when cancelled.
	 */
	private askApproveLookbackHours(): number {
		let entered = window.prompt('Approve Imported Invoices: approve every Pending invoice created in the last how many hours? (1-720)', '3');
		while (entered !== null) {
			const hours = Number(entered.trim());
			if (/^\d+$/.test(entered.trim()) && hours >= 1 && hours <= 720) {
				return hours;
			}
			entered = window.prompt('Enter a whole number of hours from 1 to 720.', '3');
		}
		return null;
	}

	private startMigration(selected: ConversionClient[], part: number) {
		// The full conversion runs step 17, so it asks how many hours back that step looks.
		if (part === 3) {
			const hours = this.askApproveLookbackHours();
			if (hours === null) {
				return;
			}
			this.approveLookbackHours = hours;
		} else {
			this.approveLookbackHours = null;
		}

		this.migrationRunning = true;
		this.migrationPart = part;
		this.migrationCurrentName = '';
		this.migrationCompleted = 0;
		this.migrationTotal = selected.length;
		this.migrationResults = [];
		this.showMigrationResults = false;

		selected.forEach((row) => {
			row.migrationStatus = 'Pending';
			row.migrationSummary = '';
		});

		// One carrier per request, run in sequence: a carrier with a lot of invoices
		// makes a lot of FactorCloud calls, and queueing them all at once would both
		// hammer the API and make a timeout impossible to attribute.
		this.migrateNext(selected, 0, part);
	}

	private migrateNext(queue: ConversionClient[], index: number, part: number) {
		if (index >= queue.length) {
			this.finishMigration(part, queue);
			return;
		}

		const row = queue[index];
		row.migrating = true;
		row.migrationStatus = 'Running';
		this.migrationCurrentName = row.clientName;

		// Every run gets the live progress modal. In a batch it stays open and moves on to
		// each carrier in turn; the results panel still lists every carrier afterwards.
		const showProgress = !!this.migrationProgressModal;
		// Always needed now: the run happens in the background and is followed by its run id.
		const runId = this.newRunId();
		const isLast = index === queue.length - 1;
		if (showProgress) {
			this.ngZone.run(() => this.migrationProgressModal.open(row, runId, part, index + 1, queue.length));
		}

		this.runMigration(row.clientKeys, runId, part)
			.then(
				(result: ConversionMigrationResult) => {
					row.migrating = false;
					this.migrationCompleted++;

					if (showProgress) {
						this.migrationProgressModal.requestFinished(result, result ? null : 'No result returned.', isLast);
					}

					if (result) {
						this.migrationResults.push(result);
						row.migrationStatus = result.ignored
							? 'Ignored'
							: result.successful
							? part === 1
								? 'Part 1 Done'
								: 'Migrated'
							: 'Failed';
						row.migrationSummary = result.summary;

						if (result.successful) {
							row.selected = false;
							if (part === 1) {
								row.conversionPart1CompletedDate = new Date().toISOString();
							}
						}
					} else {
						row.migrationStatus = 'Failed';
						row.migrationSummary = 'No result returned.';
						this.migrationResults.push(this.requestFailureResult(row, row.migrationSummary));
					}

					// Step 1 stopped on "The company code X is already registered": pause the run and pop up who has
					// the code, with the option to re-code that company and run this carrier again.
					if (this.hasCodeConflict(result)) {
						this.askToResolveCodeConflict(result, row).then((retry) => {
							if (retry) {
								// This attempt's result is replaced by the re-run's.
								const attempt = this.migrationResults.indexOf(result);
								if (attempt >= 0) {
									this.migrationResults.splice(attempt, 1);
								}
								this.migrationCompleted--;
								// A single carrier (or the first of a batch) opens a fresh progress modal on the re-run.
								if (showProgress && index === 0) {
									this.migrationProgressModal.close();
								}
								this.migrateNext(queue, index, part);
							} else {
								this.migrateNext(queue, index + 1, part);
							}
						});
						return;
					}

					this.migrateNext(queue, index + 1, part);
				},
				(error) => {
					row.migrating = false;
					this.migrationCompleted++;
					row.migrationStatus = 'Failed';
					row.migrationSummary =
						error && error.error ? error.error : 'The migration request failed.';

					if (showProgress) {
						this.migrationProgressModal.requestFinished(
							null,
							typeof row.migrationSummary === 'string'
								? row.migrationSummary
								: 'The migration request failed.',
							isLast
						);
					}

					// No step list comes back from a failed request, but the carrier still
					// belongs in the results panel so the operator can see it did not run.
					this.migrationResults.push(this.requestFailureResult(row, row.migrationSummary));

					ConsoleLogger.logInfo('conversion.migrateSelected error', error);

					// Keep going — one carrier failing should not stop the rest of the batch.
					this.migrateNext(queue, index + 1, part);
				}
			);
	}

	/** Step 1 failed because another FactorCloud company already has the carrier's code. */
	private hasCodeConflict(result: ConversionMigrationResult): boolean {
		return !!(
			result &&
			!result.successful &&
			result.failedAtStep === 1 &&
			result.codeConflict &&
			result.codeConflict.owners &&
			result.codeConflict.owners.length > 0
		);
	}

	/**
	 * Pops up the code conflict. Resolves true once the owning company has been re-coded (the carrier is then
	 * run again), or false when the operator skips it (the run moves on to the next carrier).
	 */
	private askToResolveCodeConflict(result: ConversionMigrationResult, row: ConversionClient): Promise<boolean> {
		this.codeConflict = result.codeConflict;
		this.codeConflictClientName = result.clientName || (row ? row.clientName : '');
		this.recodeRunning = false;
		this.recodeError = '';

		return new Promise<boolean>((resolve) => {
			this.ngZone.run(() => {
				this.codeConflictModalRef = this.modalService.open(this.codeConflictModalTemplate, {
					container: '.app',
					size: 'lg',
					backdrop: 'static',
					keyboard: false,
				});
				this.codeConflictModalRef.result.then(
					(retry) => resolve(retry === true),
					() => resolve(false)
				);
			});
		});
	}

	/** Only one company can be re-coded from the popup; with more than one, they are fixed in FactorCloud. */
	get codeConflictOwner(): ConversionCodeOwner {
		return this.codeConflict && this.codeConflict.owners && this.codeConflict.owners.length === 1
			? this.codeConflict.owners[0]
			: null;
	}

	/** Popup button: changes the owning company's code to code + 8888, then the carrier is run again. */
	recodeConflictOwner() {
		const owner = this.codeConflictOwner;
		if (!owner || this.recodeRunning) {
			return;
		}

		this.recodeRunning = true;
		this.recodeError = '';
		this.conversionService
			.recodeCompany(owner.companyType, owner.id, this.codeConflict.code)
			.toPromise()
			.then(
				(recoded: ConversionRecodeCompanyResult) => {
					this.recodeRunning = false;
					this.toastrService.success(
						`${recoded.companyName} is now code ${recoded.newCode}. Running ${this.codeConflictClientName} again with code ${recoded.oldCode}.`,
						'Company Code Changed'
					);
					this.closeCodeConflict(true);
				},
				(error) => {
					this.recodeRunning = false;
					this.recodeError =
						error && typeof error.error === 'string' ? error.error : 'The company code could not be changed.';
					ConsoleLogger.logInfo('conversion.recodeConflictOwner error', error);
				}
			);
	}

	/** Closes the popup: true runs the carrier again, false skips it. */
	closeCodeConflict(retry: boolean) {
		if (this.recodeRunning) {
			return;
		}
		if (this.codeConflictModalRef) {
			this.codeConflictModalRef.close(retry);
			this.codeConflictModalRef = null;
		}
	}

	/**
	 * Counts only the carriers that ran in this batch: a row can keep a 'Failed' status from an
	 * earlier batch (loadConversionClients restores it), and that must not turn this batch's
	 * summary into "Finished With Problems".
	 */
	private finishMigration(part: number, queue: ConversionClient[]) {
		this.migrationRunning = false;

		const failed = queue.filter(
			(row) => row.migrationStatus === 'Failed'
		).length;
		const done = queue.filter(
			(row) => row.migrationStatus === (part === 1 ? 'Part 1 Done' : 'Migrated')
		).length;
		const doneText = part === 1 ? 'through Part 1' : 'converted';

		if (failed === 0) {
			this.toastrService.success(
				part === 1
					? `${done} carrier(s) through Part 1. Import their invoices into FactorCloud, then run Part 2.`
					: `${done} carrier(s) converted.`,
				`${this.partLabel(part)} Complete`
			);
		} else {
			this.toastrService.warning(
				`${done} ${doneText}, ${failed} need attention. The results panel above the list shows which step stopped.`,
				`${this.partLabel(part)} Finished With Problems`
			);
		}

		// The reload replaces this.rows, which wipes each row's migrationStatus and
		// migrationSummary, and completed carriers drop out of the queue entirely. The
		// per-step results live in migrationResults instead, which the reload leaves alone.
		this.showMigrationResults = this.migrationResults.length > 0;

		// Converted carriers (Part 2) drop out of the queue. Carriers through Part 1 stay listed,
		// waiting for their invoices to be imported and Part 2 to run; partly run ones stay so
		// they can be run again to resume.
		this.loadConversionClients();
	}

	/**
	 * Starts the run on the server, which works through it in the background, and resolves
	 * with its result once the progress endpoint reports it complete. A run that ends without
	 * a result rejects with the message in error.error, the same shape as a failed request.
	 */
	private runMigration(clientKeys: number[], runId: string, part: number): Promise<ConversionMigrationResult> {
		return this.conversionService
			.startMigration(clientKeys, runId, part, this.includeCompletedCarriers, this.approveLookbackHours)
			.toPromise()
			.then(() => this.waitForMigration(runId));
	}

	private waitForMigration(runId: string): Promise<ConversionMigrationResult> {
		const pollMs = 2000;
		// The run is registered before Start answers, so a minute of "not found" means the
		// server lost it - most likely it restarted.
		const maxNotFound = 30;
		// The run keeps going on the server even when a poll fails (a busy server can be slow to
		// answer while a large carrier is converting), so only about five minutes of failed polls
		// in a row count as the server being unreachable.
		const maxErrors = 150;

		return new Promise<ConversionMigrationResult>((resolve, reject) => {
			let notFound = 0;
			let errors = 0;

			const check = () => {
				this.conversionService
					.getMigrationProgress(runId)
					.toPromise()
					.then(
						(progress: ConversionMigrationProgress) => {
							errors = 0;
							if (!progress) {
								notFound++;
								if (notFound >= maxNotFound) {
									reject({
										error: 'The server lost track of this run (it may have restarted). Refresh the list and run the carrier again - it picks up where it stopped.',
									});
									return;
								}
							} else {
								notFound = 0;
								if (progress.isComplete) {
									if (progress.result) {
										resolve(progress.result);
									} else {
										reject({ error: progress.errorMessage || 'The migration could not be run.' });
									}
									return;
								}
							}
							setTimeout(check, pollMs);
						},
						(error) => {
							errors++;
							if (errors >= maxErrors) {
								reject(error);
								return;
							}
							setTimeout(check, pollMs);
						}
					);
			};

			setTimeout(check, 1000);
		});
	}

	/** Identifies a carrier row across reloads: its client keys. */
	private rowKey(row: ConversionClient): string {
		return (row.clientKeys || []).slice().sort((a, b) => a - b).join(',');
	}

	private newRunId(): string {
		return (
			Date.now().toString(36) +
			'-' +
			Math.random().toString(36).substring(2, 10) +
			Math.random().toString(36).substring(2, 10)
		);
	}

	/** Whole-batch progress for the on-page panel, never 0 so the bar is visible from the start. */
	get migrationPercent(): number {
		if (!this.migrationTotal) {
			return 0;
		}
		return Math.max(5, Math.round((this.migrationCompleted / this.migrationTotal) * 100));
	}

	/** What the current carrier is doing right now, read from the progress modal's last poll. */
	get migrationActivity(): string {
		const modal = this.migrationProgressModal;
		const progress = modal ? modal.progress : null;
		if (!progress || !progress.activity) {
			return 'Starting...';
		}
		const counts = progress.total ? ` (${progress.completed || 0} of ${progress.total})` : '';
		return progress.currentStep
			? `Step ${progress.currentStep}: ${progress.activity}${counts}`
			: progress.activity + counts;
	}

	dismissMigrationResults() {
		this.showMigrationResults = false;
	}

	private requestFailureResult(row: ConversionClient, message: string): ConversionMigrationResult {
		const result = new ConversionMigrationResult();
		result.clientName = row.clientName;
		result.clientKeys = row.clientKeys;
		result.successful = false;
		result.summary = typeof message === 'string' ? message : 'The migration request failed.';
		result.steps = [];
		return result;
	}

	migrationResultClass(result: ConversionMigrationResult) {
		if (result.successful) {
			return 'text-success';
		}
		return result.steps && result.steps.length > 0 ? 'text-warning' : 'text-danger';
	}

	/** Negative invoices skipped across every result shown, or one result's when given. */
	skippedInvoices(result?: ConversionMigrationResult): ConversionSkippedInvoice[] {
		return skippedNegativeInvoices(result ? [result] : this.migrationResults);
	}

	/**
	 * Downloads the negative invoices the conversion skipped as a CSV, for the account rep:
	 * one carrier's when a result is given, otherwise every carrier in the results.
	 */
	downloadSkippedInvoices(result?: ConversionMigrationResult) {
		downloadSkippedInvoicesCsv(this.skippedInvoices(result), result ? result.clientName : 'all_carriers');
	}

	/** Exceptions (e.g. invalid EINs skipped) across every result shown, or one result's when given. */
	exceptions(result?: ConversionMigrationResult): ConversionException[] {
		return conversionExceptions(result ? [result] : this.migrationResults);
	}

	/** Downloads the carriers the grid shows (current tab and filters) as a CSV. */
	exportGrid() {
		const tab = this.showRemoved
			? 'archived'
			: this.view === 5
			? 'all'
			: this.view === 4
			? 'not_yet_converted'
			: this.view === 3
			? 'converted'
			: this.view === 2
			? 'part_2'
			: 'part_1';
		downloadConversionListCsv(this.filteredRows, (row) => this.gridStatus(row), tab);
	}

	/** The status text the grid's Status column shows for a carrier. */
	gridStatus(row: ConversionClient): string {
		if (row.migrating) {
			return 'Running';
		}
		if (row.migrationStatus) {
			return row.migrationStatus;
		}
		if (row.conversionCompletedDate) {
			return 'Converted';
		}
		if (row.conversionPart1CompletedDate) {
			return 'Awaiting Invoices';
		}
		return this.showRemoved ? 'Archived' : 'Not Started';
	}

	/** Downloads the exception file: one carrier's when a result is given, otherwise every carrier's. */
	downloadExceptions(result?: ConversionMigrationResult) {
		downloadExceptionsCsv(this.exceptions(result), result ? result.clientName : 'all_carriers');
	}

	/** A step detail split into its notes (the server puts each note on its own line). */
	public detailLines(detail: string): string[] {
		return (detail || '')
			.split('\n')
			.map((line) => line.trim())
			.filter((line) => line.length > 0);
	}

	migrationStepClass(step: ConversionMigrationStep) {
		if (step.status === 'Failed') {
			return 'text-danger font-weight-bold';
		}
		if (step.status === 'Skipped') {
			return 'text-muted';
		}
		return 'text-success';
	}

	migrationStatusClass(row: ConversionClient) {
		if (row.migrationStatus === 'Migrated' || row.migrationStatus === 'Part 1 Done') {
			return 'text-success font-weight-bold';
		}
		if (row.migrationStatus === 'Failed') {
			return 'text-danger font-weight-bold';
		}
		return 'text-muted';
	}
}



// WEBPACK FOOTER //
// ./src/app/portal/conversion/conversion.component.ts